import * as React from 'react';
import { Button, type ButtonProps } from './button.js';

/**
 * Shared ASK_AVAILABILITY popover actions: an always-visible "enquire" mailto
 * link plus an optional admin-only "set status manually" action. Extracted
 * from near-duplicate JSX in the media-units and offers features — both
 * rendered the same enquiry + manual-status pattern with the same Tailwind
 * classes for their respective ASK_AVAILABILITY cell popovers.
 *
 * Props-only and translation-free: callers resolve labels/hrefs via their own
 * i18n setup and pass the resolved strings in.
 */
export interface AskAvailabilityActionsProps {
  /** data-testid for the outer wrapper. */
  containerTestId: string;
  /** Translated "enquire availability" link text. */
  enquireLabel: string;
  /** Precomputed `mailto:?subject=...` href for the enquire link. */
  enquireHref: string;
  /** data-testid for the enquire link. */
  enquireLinkTestId: string;
  /** Whether the caller is permissioned to set the status manually. */
  canManage: boolean;
  /** Translated "set status manually" label. */
  setStatusManuallyLabel: string;
  /** data-testid for the set-status-manually button. */
  setStatusManuallyTestId: string;
  /** Called when the set-status-manually action is clicked. */
  onSetStatusManually?: () => void;
  /**
   * Forwarded to the outer wrapper's onClick. Callers nested inside another
   * clickable trigger chain (e.g. a Radix Popover trigger) can use this to
   * stop click propagation.
   */
  onContainerClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  /**
   * Button `size` for the "set status manually" action. Defaults to `'sm'`,
   * preserving the media-units call site's pre-extraction behavior. The
   * offers call site previously had no `size` prop (Button's `default`) and
   * must pass `'default'` explicitly to avoid a visual regression.
   */
  setStatusManuallySize?: ButtonProps['size'];
}

export function AskAvailabilityActions({
  containerTestId,
  enquireLabel,
  enquireHref,
  enquireLinkTestId,
  canManage,
  setStatusManuallyLabel,
  setStatusManuallyTestId,
  onSetStatusManually,
  onContainerClick,
  setStatusManuallySize = 'sm',
}: AskAvailabilityActionsProps) {
  return (
    <div
      className="p-3 space-y-2 text-[13px]"
      data-testid={containerTestId}
      onClick={onContainerClick}
    >
      <a
        data-testid={enquireLinkTestId}
        href={enquireHref}
        className="block text-primary underline hover:no-underline"
      >
        {enquireLabel}
      </a>
      {canManage ? (
        <Button
          type="button"
          variant="ghost"
          size={setStatusManuallySize}
          className="block h-auto p-0 text-left text-muted-foreground hover:text-foreground transition-colors"
          data-testid={setStatusManuallyTestId}
          onClick={onSetStatusManually}
        >
          {setStatusManuallyLabel}
        </Button>
      ) : null}
    </div>
  );
}
